import React, { useState, useEffect } from 'react'
import {
  Clock,
  Gift,
  CreditCard
} from 'lucide-react'
import { useAuth } from '../../context/AuthContext'
import { supabase } from '../../lib/supabase'

interface CreditTransaction {
  id: string
  transaction_type: 'purchase' | 'usage' | 'refund' | 'bonus' | 'gift'
  credits_change: number
  credits_before: number
  credits_after: number
  description: string
  reference_id: string | null
  created_at: string
}

interface CreditHistoryProps {
  limit?: number
  showFilters?: boolean
  className?: string
}

const CreditHistory: React.FC<CreditHistoryProps> = ({ 
  limit = 20, 
  showFilters = true,
  className = '' 
}) => {
  const { user } = useAuth()
  const [transactions, setTransactions] = useState<CreditTransaction[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState<string>('all')
  const [sortOrder, setSortOrder] = useState<'desc' | 'asc'>('desc')

  useEffect(() => {
    if (user) {
      loadTransactions()
    }
  }, [user, filter, sortOrder])

  const loadTransactions = async () => {
    if (!user) return
    
    try {
      setLoading(true)
      
      const { data, error } = await supabase.rpc('get_user_credit_history', {
        user_id_param: user.id,
        limit_param: limit,
        offset_param: 0
      })

      if (error) {
        console.error('Error loading credit history:', error)
        return
      }

      let filteredData = data || []
      
      // Apply filter
      if (filter !== 'all') {
        filteredData = filteredData.filter((t: CreditTransaction) => t.transaction_type === filter)
      }
      
      // Apply sort
      filteredData.sort((a: CreditTransaction, b: CreditTransaction) => {
        const dateA = new Date(a.created_at).getTime()
        const dateB = new Date(b.created_at).getTime()
        return sortOrder === 'desc' ? dateB - dateA : dateA - dateB
      })

      setTransactions(filteredData)
    } catch (error) {
      console.error('Error loading credit history:', error)
    } finally {
      setLoading(false)
    }
  }

  const getTransactionIcon = (type: string) => {
    switch (type) {
      case 'purchase':
        return <CreditCard className="w-4 h-4 text-green-600" />
      case 'generation':
        return <Zap className="w-4 h-4 text-blue-600" />
      case 'bonus':
        return <Gift className="w-4 h-4 text-purple-600" />
      case 'refund':
        return <ArrowUp className="w-4 h-4 text-orange-600" />
      default:
        return <CreditCard className="w-4 h-4 text-gray-600" />
    }
  }

  const getTransactionColor = (change: number) => {
    return change > 0 ? 'text-green-600' : 'text-red-600'
  }

  const formatTransactionType = (type: string) => {
    switch (type) {
      case 'purchase':
        return 'Credit Purchase'
      case 'usage':
        return 'Image Generation'
      case 'gift':
        return 'Gift Received'
      case 'bonus':
        return 'Bonus Credits'
      case 'refund':
        return 'Refund'
      default:
        return type.charAt(0).toUpperCase() + type.slice(1)
    }
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffTime = Math.abs(now.getTime() - date.getTime())
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

    if (diffDays === 1) {
      return 'Today'
    } else if (diffDays === 2) {
      return 'Yesterday'
    } else if (diffDays <= 7) {
      return `${diffDays - 1} days ago`
    } else {
      return date.toLocaleDateString()
    }
  }

  if (loading) {
    return (
      <div className={`bg-white rounded-lg shadow-sm border border-gray-200 p-6 ${className}`}>
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-gray-200 rounded-full"></div>
                <div className="flex-1">
                  <div className="h-4 bg-gray-200 rounded w-1/2 mb-1"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/3"></div>
                </div>
                <div className="h-4 bg-gray-200 rounded w-16"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={`bg-white rounded-lg shadow-sm border border-gray-200 ${className}`}>
      {/* Header */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <Clock className="w-5 h-5 mr-2 text-gray-500" />
            Credit History
          </h3>
          <button
            onClick={loadTransactions}
            className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>

        {/* Filters */}
        {showFilters && (
          <div className="flex flex-wrap items-center space-x-4">
            <div className="flex items-center space-x-2">
              <Filter className="w-4 h-4 text-gray-500" />
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="text-sm border border-gray-300 rounded px-3 py-1 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Transactions</option>
                <option value="purchase">Purchases</option>
                <option value="usage">Usage</option>
                <option value="gift">Gifts</option>
                <option value="bonus">Bonuses</option>
                <option value="refund">Refunds</option>
              </select>
            </div>

            <div className="flex items-center space-x-2">
              <Calendar className="w-4 h-4 text-gray-500" />
              <select
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value as 'desc' | 'asc')}
                className="text-sm border border-gray-300 rounded px-3 py-1 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="desc">Newest First</option>
                <option value="asc">Oldest First</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Transactions List */}
      <div className="p-6">
        {transactions.length === 0 ? (
          <div className="text-center py-8">
            <Zap className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h4 className="text-lg font-medium text-gray-900 mb-2">No Transactions Yet</h4>
            <p className="text-gray-500">
              Your credit transactions will appear here once you start using Toefetti.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {transactions.map((transaction) => (
              <div
                key={transaction.id}
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center space-x-4">
                  <div className="p-2 bg-white rounded-full shadow-sm">
                    {getTransactionIcon(transaction.transaction_type)}
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <h4 className="font-medium text-gray-900">
                        {formatTransactionType(transaction.transaction_type)}
                      </h4>
                      <span className={`text-sm font-medium ${getTransactionColor(transaction.credits_change)}`}>
                        {transaction.credits_change > 0 ? '+' : ''}{transaction.credits_change}
                      </span>
                    </div>
                    
                    <p className="text-sm text-gray-600 mt-1">
                      {transaction.description}
                    </p>
                    
                    <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500">
                      <span>{formatDate(transaction.created_at)}</span>
                      <span>•</span>
                      <span>Balance: {transaction.credits_after}</span>
                      {transaction.reference_id && (
                        <>
                          <span>•</span>
                          <span className="font-mono">
                            {transaction.reference_id.slice(0, 8)}...
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                <div className="text-right">
                  <div className={`text-lg font-bold ${getTransactionColor(transaction.credits_change)}`}>
                    {transaction.credits_change > 0 ? '+' : ''}{transaction.credits_change}
                  </div>
                  <div className="text-xs text-gray-500">
                    credits
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default CreditHistory 